import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://sanatandharmaonline.com";
const BRAND = "Sanatan Dharma™";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;
const GMB_URL = "https://share.google/G3Tbwzn4cIYvQ624h";
const INSTAGRAM_URL = "https://www.instagram.com/sanatandharma_online";
const ADDRESS = {
  street: "Second Floor, Kh No. 26/3, Main Seva Dhaam Road, Mandoli",
  locality: "Delhi",
  region: "Delhi",
  postalCode: "110093",
  country: "IN",
};

/**
 * Reusable SEO component. Every page passes:
 *  - title
 *  - description
 *  - path (canonical)
 *  - image (optional)
 *  - keywords (optional)
 *  - jsonLd (optional structured-data object or array)
 */
const SEO = ({ title, description, path = "/", image = DEFAULT_OG, keywords, jsonLd }) => {
  const fullTitle = title.includes("Sanatan Dharma") ? title : `${title} | ${BRAND}`;
  const canonical = `${SITE_URL}${path}`;
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {ldArray.map((ld, i) => (
        <script type="application/ld+json" key={i}>{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
};

export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Sanatan Dharma",
  alternateName: ["Sanatan Dharma — Har Ghar Ka Vishwas", "Sara Greenworks Pvt. Ltd."],
  legalName: "Sara Greenworks Pvt. Ltd.",
  url: SITE_URL,
  logo: `${SITE_URL}/brand/logo.png`,
  slogan: "Har Ghar Ka Vishwas",
  description: "Premium ritual essentials — kapoor, kumkum, kalava, agarbatti, puja oil, sambrani cups, gangajal, havan samagri and complete puja kits crafted with authenticity and trust for every Indian home.",
  foundingLocation: "Delhi, India",
  address: {
    "@type": "PostalAddress",
    streetAddress: ADDRESS.street,
    addressCountry: ADDRESS.country,
    addressLocality: ADDRESS.locality,
    addressRegion: ADDRESS.region,
    postalCode: ADDRESS.postalCode,
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-7982444690",
    contactType: "customer service",
    email: "info@sanatandharmaonline.com",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi"],
  },
  sameAs: [
    GMB_URL,
    INSTAGRAM_URL,
    "https://www.indiamart.com/saragreenworksprivatelimited-newdelhi/",
  ],
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "Store",
  "@id": `${SITE_URL}/#store`,
  name: "Sanatan Dharma — A Unit of Sara Greenworks Pvt. Ltd.",
  image: `${SITE_URL}/brand/logo.png`,
  logo: `${SITE_URL}/brand/logo.png`,
  url: SITE_URL,
  telephone: "+91-7982444690",
  email: "info@sanatandharmaonline.com",
  priceRange: "₹₹",
  address: {
    "@type": "PostalAddress",
    streetAddress: ADDRESS.street,
    addressCountry: ADDRESS.country,
    addressLocality: ADDRESS.locality,
    addressRegion: ADDRESS.region,
    postalCode: ADDRESS.postalCode,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 28.6969,
    longitude: 77.3260,
  },
  hasMap: GMB_URL,
  openingHoursSpecification: [{
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "10:00", closes: "19:00",
  }],
  sameAs: [
    GMB_URL,
    INSTAGRAM_URL,
    "https://www.indiamart.com/saragreenworksprivatelimited-newdelhi/",
  ],
  areaServed: { "@type": "Country", name: "India" },
};

export { GMB_URL, INSTAGRAM_URL, ADDRESS };

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: BRAND,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/products?search={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const productSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  image: `${SITE_URL}${product.image}`,
  description: product.short,
  brand: { "@type": "Brand", name: "Sanatan Dharma" },
  category: product.category,
  sku: product.slug,
  mpn: `SD-${product.slug.toUpperCase()}`,
  ...(product.sizes && {
    hasVariant: product.sizes.map((s) => ({
      "@type": "Product",
      name: `${product.name} — ${s}`,
      sku: `${product.slug}-${s.toLowerCase().replace(/\s+/g, "-")}`,
    })),
  }),
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "INR",
    availability: "https://schema.org/InStock",
    seller: { "@type": "Organization", name: "Sanatan Dharma" },
    url: `${SITE_URL}/products/${product.slug}`,
    priceValidUntil: "2027-12-31",
    offerCount: (product.sizes || []).length || 1,
  },
});

export const faqSchema = (faqList) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqList || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const articleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: post.title,
  description: post.excerpt,
  image: `${SITE_URL}${post.image}`,
  datePublished: post.date,
  author: { "@type": "Organization", name: "Sanatan Dharma" },
  publisher: {
    "@type": "Organization",
    name: "Sanatan Dharma",
    logo: { "@type": "ImageObject", url: `${SITE_URL}/brand/logo.png` },
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export default SEO;
